import { Tabs } from "antd";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DashServices from "../../services/DashServices";
import BloodGlucoseCharts from "../Charts/BloodGlucoseCharts";
import BloodPressureCharts from "../Charts/BloodPressureCharts";
import O2SaturationCharts from "../Charts/O2SaturationCharts";
import StepsCharts from "../Charts/StepsCharts";
import TemperatureCharts from "../Charts/TemperatureCharts";
import WeightCharts from "../Charts/WeightCharts";
import FullPageLoader from "../Loaders/FullPageLoader";

const PatientSelfMeasuresTable = () => {
  const location = useLocation();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultActiveKey, setDefaultActiveKey] = useState(null);
  const [isFrOn, setIsFrOn] = useState(false);
  const [modulesKeys, setModulesKeys] = useState({
    hipertension: null,
    diabetes: null,
    pesos: null,
    saturacionO2: null,
    temperatura: null,
    pasos: null,
  });

  const getModuleIndex = useCallback((moduleId, modules) => {
    return modules.findIndex((m) => m.module === moduleId);
  }, []);
  
  const itemFilter = useCallback((array, uniqueModules) => {
    return array.filter((item) => !uniqueModules.some((m) => m.name === item.name));
  }, []);

  const getUniqueModules = useCallback((data) => {
    let uniqueModules = [];
    data.forEach((item) => {
      uniqueModules = uniqueModules.concat(itemFilter(item, uniqueModules));
    });

    return uniqueModules;
  }, [itemFilter]);

  const loadSubscription = useCallback(async (ids) => {
    setLoading(true);
    try {
      const fetchPromises = ids.map((i) => DashServices.getSubscriptions(i));
      const res = await Promise.all(fetchPromises);
      const data = await Promise.all(
        res.map((r) => {
          if (r.status != 200) return;
          return r.data.subscription.modules;
        })
      );

      const o2Module = data[0].find((m) => m.name === "SaturacionO2");
      if (o2Module) {
        const frSubmodule = o2Module.submodules?.some(
          (submodule) =>
            submodule.label === "Frecuencia Respiratoria" && submodule.selected
        );
        setIsFrOn(!!frSubmodule);
      }

      let newModules = data[0];

      const uniqueModules = getUniqueModules(data);
      newModules = uniqueModules;

      setModules(newModules.map((i) => i.module));
      const modulesObject = newModules.reduce((acc, item) => {
        acc[_.kebabCase(item.name)] = item.module;
        return acc;
      }, {});
      setModulesKeys(modulesObject);
      const tabKeyOrder = [
        modulesObject.hipertension ? modulesObject.hipertension : null,
        modulesObject.diabetes ? modulesObject.diabetes : null,
        modulesObject.pesos ? modulesObject.pesos : null,
        modulesObject["saturacion-o-2"]
          ? modulesObject["saturacion-o-2"]
          : null,
        modulesObject.temperatura ? modulesObject.temperatura : null,
        modulesObject.pasos ? modulesObject.pasos : null,
      ];
      let selected = null;
      tabKeyOrder.forEach((i) => {
        const index = getModuleIndex(i, newModules);
        if (index && selected == null) {
          selected = i;
        }
      });
      setDefaultActiveKey(selected);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [getModuleIndex, getUniqueModules]);

  useEffect(() => {
    if (location.state.subscriptions) {
      loadSubscription(location.state.subscriptions.map((i) => i.id));
    }
  }, [location.state.subscriptions, loadSubscription, getModuleIndex, itemFilter, getUniqueModules]);

  const items = [
    {
      label: "Presión arterial",
      key:
        modulesKeys.hipertension == null
          ? "hipertension"
          : modulesKeys.hipertension.toString(),
      disabled: !modules.includes(modulesKeys.hipertension),
      children: <BloodPressureCharts />,
    },
    {
      label: "Glucemia",
      key:
        modulesKeys.diabetes == null
          ? "diabetes"
          : modulesKeys.diabetes.toString(),
      disabled: !modules.includes(modulesKeys.diabetes),
      children: <BloodGlucoseCharts />,
    },
    {
      label: "Peso",
      key: modulesKeys.pesos == null ? "pesos" : modulesKeys.pesos.toString(),
      disabled: !modules.includes(modulesKeys.pesos),
      children: <WeightCharts />,
    },
    {
      label: "Saturación",
      key:
        modulesKeys["saturacion-o-2"] == null
          ? "saturacion02"
          : modulesKeys["saturacion-o-2"].toString(),
      disabled: !modules.includes(modulesKeys["saturacion-o-2"]),
      children: <O2SaturationCharts isFrOn={isFrOn} />,
    },
    {
      label: "Temperatura",
      key:
        modulesKeys.temperatura == null
          ? "temperatura"
          : modulesKeys.temperatura.toString(),
      disabled: !modules.includes(modulesKeys.temperatura),
      children: <TemperatureCharts />,
    },
    {
      label: "Pasos",
      key: modulesKeys.pasos == null ? "pasos" : modulesKeys.pasos.toString(),
      disabled: !modules.includes(modulesKeys.pasos),
      children: <StepsCharts />,
    },
  ];
  return (
    <>
      {!loading && defaultActiveKey != null && (
        <Tabs
          defaultActiveKey={defaultActiveKey.toString()}
          style={{ maxWidth: "100%", borderwidth: 1, borderColor: "black" }}
          items={items}
        />
      )}
      {loading && <FullPageLoader />}
    </>
  );
};

export default PatientSelfMeasuresTable;
